import { mouse, select } from 'd3-selection';
import { max, min, extent, sum } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { axisBottom } from 'd3-axis';
import { area, curveBasis, stack } from 'd3-shape';
import { drag } from 'd3-drag';
import interpolate from 'd3-interpolate-path';
import _indexOf from 'lodash/indexOf';

// Utils
import { minTickSize, maxTickSize } from 'utils/chart';
import { regionColors, axisColor, textColor, markerColor, colorGoMarker, backgroundAreaColor } from 'utils/colors';
import { textStyle } from 'utils/styles';

export default class Timeline {
  constructor(svg, dragSvg) {
    this.margin = {
      top: 20,
      left: 20,
      bottom: 50,
      right: 50,
    };
    this.duration = 800;


    this.xScale = scaleLinear();

    svg
      .on('mousemove', (d, i, el) => {
        const xpos = mouse(el[i])[0];
        this._setGoMarker(xpos);
      })
      .on('mouseout', () => {
        this._setGoMarker();
      });

    this.g = svg.append('g')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
    this.g
      .style('opacity', 0)
      .transition()
      .duration(this.duration)
      .style('opacity', 1);

    dragSvg.call(
      drag()
        .on('start', () => {
          this._onclickMarker();
        })
        .on('drag', () => {
          this.dragging = true;
          const xpos = mouse(svg.node())[0];
          this._setGoMarker(xpos);
          this._onclickMarker();
        })
        .on('end', () => {
          this.dragging = false;
          this._setGoMarker();
        })
    )
      .on('mousemove', (d, i, el) => {
        const xpos = mouse(svg.node())[0];
        this._setGoMarker(xpos);
      })
      .on('mouseout', () => {
        this._setGoMarker();
      });
    this._createMarker();
    this._createAxis();

    this.area = area()
      .x(d => {
        return +d.data.year <= +this.options.currentYear ? this.xScale(d.data.year) : this.xScale(this.options.currentYear);
      })
      .y1(d => {
        return +d.data.year <= +this.options.currentYear ? this.yScale(d[0]) : null;
      })
      .y0(d => {
        return this.yScale(d[1]);
      });
    this.backgroundArea = area()
      .x(d => this.xScale(d.data.year))
      .y0(d => this.yScale(d[0]))
      .y1(d => this.yScale(d[1]));
    this.zeroArea = area().x(d => this.xScale(d.data.year));
    this.area.curve(curveBasis);
    this.backgroundArea.curve(curveBasis);
  }

  setOnMarkerClickFunction(onClickFunc) {
    this.onClickFunc = onClickFunc;
  }

  render(options, firstRender) {
    const { xScale, yScale, area, zeroArea, backgroundArea, margin, duration } = this;
    const { width, height } = options;
    this.options = options;
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;

    xScale.range([0, w]);
    yScale.range([h, 0]);

    if (firstRender) {
      zeroArea.y0(h).y1(h);
      this.backgroundPaths
        .attr('d', zeroArea)
        .transition()
        .duration(duration)
        .attrTween('d', d => interpolate.interpolatePath(zeroArea(d), backgroundArea(d)));
      this.pathElements
        .attr('d', zeroArea)
        .transition()
        .duration(duration)
        .attrTween('d', d => {
          const data = d.filter(d => +d.data.year <= +options.currentYear);
          return interpolate.interpolatePath(zeroArea(data), area(data));
        });
    } else {
      this.backgroundPaths
        .transition()
        .duration(this.dragging ? 0 : options.animationDelta)
        .attr('d', backgroundArea);
      this.pathElements
        .transition()
        .duration(this.dragging ? 0 : options.animationDelta)
        .attrTween('d', (d, i, el) => {
          const prevPath = select(el[i]).attr('d');
          const data = d.filter(d => +d.data.year <= +options.currentYear);
          return interpolate.interpolatePath(prevPath, area(data));
        });
    }

    this._updateAxis();
    this._updateMarker();
  }

  _createAxis() {
    const { g } = this;
    this.axisX = g.append('g')
      .attr('class', 'axis-x');
    this.domain2 = this.axisX.append('path')
      .attr('class', 'domain-2')
      .attr('stroke', axisColor);
    this.axisY = g.append('g')
      .attr('class', 'axis-y');
    this.axisY
      .append('text')
      .attr('class', textStyle);
  }

  _updateAxis() {
    const { options, margin, axisX, axisY, domain2, fontFamily } = this;
    const w = options.width - margin.left - margin.right;
    const h = options.height - margin.top - margin.bottom;
    this.xAxis = axisBottom(this.xScale)
      .tickValues(this.timelineYears)
      .tickFormat(d => (+d + 1) % 5 === 0 ? +d + 1 : '');
    axisX
      .attr('transform', `translate(${0}, ${h})`)
      .call(this.xAxis);

    axisX.selectAll('.tick line')
      .attrs({
        y2: d => d % 5 === 0 ? maxTickSize : minTickSize,
        stroke: axisColor,
      });
    axisX.selectAll('.tick text')
      .attrs({
        y: maxTickSize,
        fill: textColor,
      })
      .attr('class', textStyle)
      .attr('transform', 'translate(0, 5)')
      .style('font-family', fontFamily);
    axisX.select('.domain')
      .attrs({
        stroke: axisColor,
      });
    domain2
      .attr('transform', `translate(${0}, ${minTickSize})`)
      .attr('d', axisX.select('.domain').attr('d'));

    axisY.select('text')
      .attrs({
        transform: `translate(${w + margin.right / 2}, ${h / 2})rotate(-90)`,
        'text-anchor': 'middle',
        fill: textColor,
      })
      .text('Total patents by region')
      .style('font-family', fontFamily);
  }

  _createMarker() {
    const { g } = this;
    this.goMarker = g.append('rect')
      .attrs({
        class: 'go-marker-rect',
        height: minTickSize,
        fill: colorGoMarker,
      });
    this.marker = g.append('rect')
      .attrs({
        class: 'marker-rect',
        height: minTickSize,
        fill: markerColor,
      });
    this.backgroundAxisRect = g.append('rect')
      .attrs({
        class: 'background-axis-rect',
        x: 0,
        height: minTickSize,
        opacity: 0,
      })
      .styles({
        cursor: 'pointer',
      });
  }

  _updateMarker() {
    const { marker, backgroundAxisRect, options, margin, dragging } = this;
    const w = options.width - margin.left - margin.right;
    const h = options.height - margin.top - margin.bottom;
    const idx = _indexOf(this.years, options.currentYear);
    const markerWidth = w / (this.timelineYears.length - 1);
    marker
      .transition()
      .duration(dragging ? 0 : options.animationDelta)
      .attr('x', idx * markerWidth)
      .attr('y', h)
      .attr('width', markerWidth);
    backgroundAxisRect.attrs({
      y: h,
      width: w,
    });
  }

  _setGoMarker(xpos) {
    const { goMarker, options, margin } = this;
    if (xpos) {
      const w = options.width - margin.left - margin.right;
      const h = options.height - margin.top - margin.bottom;
      const markerWidth = w / (this.timelineYears.length - 1);
      this.idx = Math.floor((xpos - this.margin.left) / markerWidth);
      if (this.timelineYears[this.idx + 1] && this.timelineYears[this.idx]) {
        goMarker.attrs({
          x: this.idx * markerWidth,
          y: h,
          width: markerWidth,
        })
          .transition()
          .duration(100)
          .style('opacity', 1);
      }
    } else {
      goMarker
        .transition()
        .duration(100)
        .style('opacity', 0);
    }
  }

  _onclickMarker() {
    const { idx, dragging } = this;
    if (this.years[idx]) this.onClickFunc(idx - 1, dragging);
  }

  setData(data, years) {
    this.data = data;
    this.years = years;
    this.timelineYears = [(min(years.map(d => +d)) - 1).toString()].concat(years);
    this.xScale.domain(extent(this.timelineYears));
    const maxYVal = max(years, year => {
      const vals = data.map(d => +d[year]);
      return sum(vals);
    });
    this.yScale = scaleLinear().domain([0, maxYVal]);

    const region = data.map(d => d.region);
    this.pathways = years.map(d => {
      const res = {};
      res.year = d;
      region.forEach((r, i) => res[r] = data[i][d]);
      return res;
    });
    const areaData = stack().keys(region)(this.pathways);

    this.backgroundPaths = this.g.selectAll('.background-area').data(areaData)
      .enter()
      .append('path')
      .attr('class', 'background-area')
      .attrs({
        fill: backgroundAreaColor,
        opacity: 0.6,
      });

    this.pathElements = this.g.selectAll('.area').data(areaData)
      .enter()
      .append('path')
      .attr('class', 'area')
      .attrs({
        fill: d => regionColors[d.key],
        // opacity: 0.6,
      });
  }

  setFont(fontFamily) {
    this.fontFamily = fontFamily;
  }
  getWidth() { return this.width; }
  getHeight() { return this.height; }
}
